import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Bookmark, Trash2, FolderOpen, Loader2 } from 'lucide-react';
import { foodCommunityService, RecipeCollection, RecipePost } from '../services/foodCommunityService';
import { RecipePostCard } from '../components/food-community/RecipePostCard';

const RecipeCollectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState<RecipeCollection[]>([]);
  const [savedPosts, setSavedPosts] = useState<RecipePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCollection, setActiveCollection] = useState<string | null>(null);
  const [collectionPosts, setCollectionPosts] = useState<RecipePost[]>([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [colRes, savedRes] = await Promise.all([
        foodCommunityService.getCollections(),
        foodCommunityService.getSavedPosts(1, 30),
      ]);
      setCollections(colRes.collections);
      setSavedPosts(savedRes.posts);
    } catch {}
    finally { setLoading(false); }
  };

  const loadCollectionItems = async (collectionId: string) => {
    setCollectionLoading(true);
    setActiveCollection(collectionId);
    try {
      const res = await foodCommunityService.getCollectionItems(collectionId);
      setCollectionPosts(res.posts);
    } catch {}
    finally { setCollectionLoading(false); }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) return;
    setCreating(true);
    try {
      const col = await foodCommunityService.createCollection(newCollectionName.trim());
      setCollections(prev => [...prev, col]);
      setNewCollectionName('');
      setShowNewCollection(false);
    } catch {}
    finally { setCreating(false); }
  };

  const handleDeleteCollection = async (collectionId: string) => {
    if (!confirm('Stergi aceasta colectie?')) return;
    try {
      await foodCommunityService.deleteCollection(collectionId);
      setCollections(prev => prev.filter(c => c.id !== collectionId));
      if (activeCollection === collectionId) {
        setActiveCollection(null);
        setCollectionPosts([]);
      }
    } catch {}
  };

  const currentPosts = activeCollection ? collectionPosts : savedPosts;
  const currentTitle = activeCollection
    ? collections.find(c => c.id === activeCollection)?.name || 'Colectie'
    : 'Toate retetele salvate';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/food-community')}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <h1 className="font-bold text-gray-900">Colectiile mele</h1>
          <button
            onClick={() => setShowNewCollection(true)}
            className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center hover:bg-green-700 transition-colors"
          >
            <Plus size={18} className="text-white" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 pb-24">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Collections List */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-4">
              {/* All Saved */}
              <button
                onClick={() => { setActiveCollection(null); setCollectionPosts([]); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                  !activeCollection
                    ? 'bg-green-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Bookmark size={14} />
                Toate ({savedPosts.length})
              </button>

              {collections.map(col => (
                <button
                  key={col.id}
                  onClick={() => loadCollectionItems(col.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                    activeCollection === col.id
                      ? 'bg-green-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <FolderOpen size={14} />
                  {col.name} ({col.itemsCount})
                </button>
              ))}
            </div>

            {/* Section Header */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800 text-sm">{currentTitle}</h2>
              {activeCollection && (
                <button
                  onClick={() => handleDeleteCollection(activeCollection)}
                  className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                >
                  <Trash2 size={12} />
                  Sterge colectia
                </button>
              )}
            </div>

            {/* Posts */}
            {collectionLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : currentPosts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Bookmark size={28} className="text-gray-300" />
                </div>
                <h3 className="font-semibold text-gray-700 mb-1">Nicio reteta salvata</h3>
                <p className="text-sm text-gray-400 mb-4">
                  {activeCollection
                    ? 'Aceasta colectie este goala'
                    : 'Salveaza retete din feed pentru a le gasi usor'}
                </p>
                <button
                  onClick={() => navigate('/food-community')}
                  className="bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors"
                >
                  Exploreaza retete
                </button>
              </div>
            ) : (
              currentPosts.map(post => (
                <RecipePostCard key={post.id} post={post} compact />
              ))
            )}
          </>
        )}
      </div>

      {/* New Collection Modal */}
      {showNewCollection && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-2xl p-6">
            <h3 className="font-bold text-gray-900 text-lg mb-4">Colectie noua</h3>
            <input
              type="text"
              placeholder="Numele colectiei..."
              value={newCollectionName}
              onChange={e => setNewCollectionName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateCollection()}
              autoFocus
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-green-400 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowNewCollection(false); setNewCollectionName(''); }}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Anuleaza
              </button>
              <button
                onClick={handleCreateCollection}
                disabled={!newCollectionName.trim() || creating}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {creating ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Creeaza'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeCollectionsPage;
